import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { TopicLevel2UIModel } from '@examdojo/category/v2';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { SubStem } from '@examdojo/models/markscheme';
import { MarkCalculationMethod } from '@examdojo/models/markscheme-v2';
import { EntityState } from '@examdojo/state';
import { TableRowModel } from '@examdojo/supabase';
import { GradedStem, GradedStemStoreModel, QuestionGradingResultStoreModel } from '../question-grading-result.model';
import { GradedQuestionItemV2 } from '../question-v2-grading-result.model';
import { SelfGradableMark, SelfGradableMarkPoint, SelfGradableSubStem } from '../self-gradable-result-model';
import { QuestionAttemptStatusStoreModel } from './question-attempt-status.model';
import { QuestionEntityUIModel, QuestionItemQuestionImageUIModel, StimulusUIModel } from './question.model';
import { QuestionStemUIModel } from './stem.model';

export type QuestionAttemptHttpModel = Pick<
  TableRowModel<'question_attempts', 'learning'>,
  'created_at' | 'grading_result' | 'id' | 'status' | 'question_id' | 'updated_at'
>;
export type QuestionAttemptStoreModel = QuestionAttemptHttpModel;

export type QuestionAttemptChatHttpModel = Pick<
  TableRowModel<'question_attempt_chats', 'learning'>,
  'question_attempt_id' | 'post_grading_llm_chat_id' | 'pre_grading_llm_chat_id'
>;

export enum GradingErrorCode {
  RejectedImages = 'rejected_images',
  GradingError = 'grading_error',
  InternalServerError = 'internal_server_error',
  GradingLimitExceeded = 'grading_limit_exceeded',
  Unknown = 'unknown',
  Timeout = 'timeout',
}

export interface GradingError {
  error_code: GradingErrorCode;
  error_message: string;
}

export const SelfGradableGradingErrorCode = GradingErrorCode.GradingError;

export function isGradingErrorCodeValid(gradingErrorCode: string): gradingErrorCode is GradingErrorCode {
  return Object.values(GradingErrorCode).includes(gradingErrorCode as GradingErrorCode);
}

export function isSelfGradableGradingError(err: unknown) {
  return err instanceof HttpErrorResponse && (err.error as GradingError)?.error_code === SelfGradableGradingErrorCode;
}

export type QuestionAttemptChatStoreModel = QuestionAttemptChatHttpModel;

export interface QuestionAttemptEntityStoreModel {
  markCalculationMethod: MarkCalculationMethod;
  attempt: QuestionAttemptStoreModel;
  id: string;
  hasPreviousAttempts: boolean;
  attemptStatuses: QuestionAttemptStatusStoreModel[];
  gradings: QuestionGradingResultStoreModel[];
  chat: QuestionAttemptChatStoreModel | null;
  gradingError: GradingError | null;
  openCaptureDialog: boolean;
}

export type QuestionSubStemGradeableMarkschemeUIModel = SelfGradableSubStem & {
  label: string;
  topicLevel2Models: TopicLevel2UIModel[];
};

export type QuestionStemGradeableMarkschemeUIModel = QuestionSubStemGradeableMarkschemeUIModel[];

export type QuestionStemGradeableUIModel = Omit<QuestionStemUIModel, 'markscheme'> & {
  markscheme: QuestionStemGradeableMarkschemeUIModel | null;
  gradingResult: GradedStemStoreModel | null;
};

export interface QuestionAttemptEntityUIModel extends QuestionAttemptEntityStoreModel {
  question: QuestionEntityUIModel['question'] | null;
  items: Array<StimulusUIModel | QuestionStemGradeableUIModel | QuestionItemQuestionImageUIModel>;
  userVote: QuestionEntityUIModel['userVote'] | null;
  /**
   * new format question items
   */
  questionItems: GradedQuestionItemV2[];
}

export type QuestionAttemptEntityState = EntityState<
  QuestionAttemptEntityStoreModel,
  {
    openCaptureDialog: boolean;
  },
  string
>;

export function getTopicLevel3IDsAndSelfGradableForm(subStem: SubStem): {
  topicLevel3IDs: number[];
  selfGradableSubStem: SelfGradableSubStem;
} {
  const subStemTopicLevel3IdSet = new Set<number>();

  const selfGradableSubStem = {
    ...subStem,
    methods: subStem.methods.map((method) => ({
      ...method,
      mark_groups: method.mark_groups.map((markGroup) => ({
        ...markGroup,
        mark_group_alternatives: markGroup.mark_group_alternatives.map((markGroupAlternative) => ({
          ...markGroupAlternative,
          marks: markGroupAlternative.marks.map((mark) => {
            if (mark.topic_id) {
              subStemTopicLevel3IdSet.add(Number(mark.topic_id));

              return {
                ...mark,
                points: mark.points.map(
                  (point) =>
                    ({
                      ...point,
                      formCtrl: new FormControl(false, { nonNullable: true }),
                    }) as SelfGradableMarkPoint,
                ),
              };
            }
            return mark as SelfGradableMark;
          }),
        })),
      })),
    })),
  };

  return { selfGradableSubStem, topicLevel3IDs: Array.from(subStemTopicLevel3IdSet) };
}

export function convertQuestionStemMarkschemeUIModelToGradedMarkscheme(
  stemUIModels: QuestionStemGradeableUIModel[],
): GradedStem[] {
  return stemUIModels
    .map((stemUIModel) => {
      if (!stemUIModel.markscheme) {
        return null;
      }

      return {
        stem_id: stemUIModel.id,
        stem_level_feedback: '',
        stem_letter: stemUIModel.label.toLowerCase(),
        sub_stems: stemUIModel.markscheme.map((subStem) => ({
          sub_stem_letter: subStem.label ?? '',
          sub_stem_level_feedback: '',
          methods: subStem.methods.map((method) => ({
            name: '',
            mark_groups: method.mark_groups.map((markGroup) => ({
              mark_group_alternatives: markGroup.mark_group_alternatives.map((markGroupAlternative) => ({
                name: '',
                marks: markGroupAlternative.marks.map((mark) => ({
                  mark_level_feedback: '',
                  note: '',
                  points: mark.points.map((point) => ({
                    amount: point.amount,
                    type: point.type,
                    awarded_amount: point.formCtrl.value ? point.amount : 0,
                  })),
                })),
              })),
            })),
          })),
        })),
      };
    })
    .filter(isNotNullish);
}
